import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { buscarClientes } from '../../services/clienteService';
import { getTramitePorId } from '../../services/tramiteService';
import useVolverAtras from '../../hooks/useVolverAtras';

// ClienteEntity.cuil se guarda sin guiones; se formatea solo para mostrar (mismo patrón
// duplicado en varias pantallas del proyecto — ver ListadoClientes.js, VistaDetalladaCliente.js).
const formatearCuil = (cuilRaw) => {
    const digitos = String(cuilRaw || '').replace(/\D/g, '');
    if (digitos.length !== 11) return cuilRaw;
    return `${digitos.slice(0, 2)}-${digitos.slice(2, 10)}-${digitos.slice(10)}`;
};

// Vista propia a pantalla completa (ruta /busqueda, no un overlay) para que el botón/gesto
// "atrás" del navegador se comporte de forma nativa. Clientes: mismo buscarClientes(criterio)
// tolerante a tipeo que ya usa el resto de la app (nombre, apellido o CUIL). Trámites: no existe
// endpoint de texto libre hoy, solo getTramitePorId(id) — si el criterio es puramente numérico
// se intenta como ID y, si existe, se navega a /tramites/:id (TramiteLegacyRedirect ya resuelve
// el módulo correcto), sin duplicar esa lógica de resolución acá.
export default function BusquedaGlobalMobile() {
    const [criterio, setCriterio] = useState('');
    const [clientes, setClientes] = useState([]);
    const [tramiteEncontrado, setTramiteEncontrado] = useState(null);
    const [buscando, setBuscando] = useState(false);
    const debounceRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const volver = useVolverAtras('/home');

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const texto = criterio.trim();

        if (!texto) {
            setClientes([]);
            setTramiteEncontrado(null);
            setBuscando(false);
            return undefined;
        }

        debounceRef.current = setTimeout(async () => {
            setBuscando(true);
            const soloDigitos = /^\d+$/.test(texto);
            try {
                const [clientesRes, tramiteRes] = await Promise.all([
                    buscarClientes(texto).catch(() => []),
                    soloDigitos ? getTramitePorId(texto).then(() => texto).catch(() => null) : Promise.resolve(null),
                ]);
                setClientes(Array.isArray(clientesRes) ? clientesRes : []);
                setTramiteEncontrado(tramiteRes);
            } finally {
                setBuscando(false);
            }
        }, 350);

        return () => clearTimeout(debounceRef.current);
    }, [criterio]);

    const irACliente = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);

    const sinCriterio = !criterio.trim();
    const sinResultados = !buscando && !sinCriterio && clientes.length === 0 && !tramiteEncontrado;

    return (
        <div className="mob-sheet" role="dialog" aria-modal="true" aria-label="Búsqueda">
            <div className="mob-sheet-header">
                <button
                    type="button"
                    className="mob-sheet-boton-cerrar mob-tocable"
                    onClick={volver}
                    aria-label="Volver"
                >
                    <i className="bi bi-arrow-left" aria-hidden="true" />
                </button>
                <div className="mob-busqueda-input-wrap">
                    <i className="bi bi-search" aria-hidden="true" />
                    <label htmlFor="mob-busqueda-input" className="visually-hidden">Buscar cliente, CUIL o expediente</label>
                    <input
                        id="mob-busqueda-input"
                        ref={inputRef}
                        type="text"
                        inputMode="search"
                        className="mob-busqueda-input"
                        placeholder="Cliente, CUIL o N° de expediente"
                        value={criterio}
                        onChange={(e) => setCriterio(e.target.value)}
                    />
                </div>
            </div>

            <div className="mob-sheet-body">
                {buscando && (
                    <div className="mob-estado-generico">
                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                        <span>Buscando…</span>
                    </div>
                )}

                {!buscando && sinCriterio && (
                    <div className="mob-estado-generico">
                        <i className="bi bi-search" aria-hidden="true" />
                        <span>Buscá por nombre, apellido, CUIL o número de expediente</span>
                    </div>
                )}

                {sinResultados && (
                    <div className="mob-estado-generico">
                        <i className="bi bi-emoji-frown" aria-hidden="true" />
                        <span>Sin resultados para "{criterio.trim()}"</span>
                    </div>
                )}

                {!buscando && tramiteEncontrado && (
                    <>
                        <div className="mob-busqueda-grupo-titulo">Trámite</div>
                        <button
                            type="button"
                            className="mob-busqueda-item mob-tocable"
                            onClick={() => navigate(`/tramites/${tramiteEncontrado}`)}
                        >
                            <span className="mob-busqueda-item-principal">Trámite #{tramiteEncontrado}</span>
                            <span className="mob-busqueda-item-secundario">Ver detalle</span>
                        </button>
                    </>
                )}

                {!buscando && clientes.length > 0 && (
                    <>
                        <div className="mob-busqueda-grupo-titulo">Clientes</div>
                        {clientes.map((c) => (
                            <button
                                key={c.cuil}
                                type="button"
                                className="mob-busqueda-item mob-tocable"
                                onClick={() => irACliente(c.cuil)}
                            >
                                <span className="mob-busqueda-item-principal">{c.apellido} {c.nombre}</span>
                                <span className="mob-busqueda-item-secundario">{formatearCuil(c.cuil)}</span>
                            </button>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}
